/**
 * A/B testing client (D-010 marketing growth).
 *
 * Boot once at app start: `loadExperiments()` fires GET /api/exp/active
 * with the visitor's cookieId. Returns a stable map of variant assignments.
 * After boot, route handlers and components call `getVariant('home_hero_v1')`
 * to read the visitor's variant + payload — and `exposeVariant(...)` once
 * the variant has actually rendered, so admin stats reflect real exposures
 * (not just page-loads where the experiment didn't fire).
 *
 * Conversion events are mostly server-side (auth, paid IPN). For purely
 * client-side conversions the page calls
 * `trackEvent('<name>')` here, which forwards to /api/exp/event.
 *
 * Design notes
 * ── Boot is fire-and-forget; `getVariant()` returns null until the
 *    response lands. Pages opt-in: render control while waiting,
 *    re-render when assignments arrive (we emit `roamzy:exp-loaded` once).
 * ── If /api/exp/active fails (e.g. backend cold start), pages stay on
 *    control. Don't block the user on stats infra.
 * ── `exposeVariant` is idempotent — backend has unique index on
 *    (experimentId, cookieId).
 */
import { getCookieId } from './utm';

interface VariantPick {
  experimentKey: string;
  variantKey: string;
  payload: Record<string, unknown> | null;
}

const cache = new Map<string, VariantPick>();
let primed = false;
const exposed = new Set<string>();   // dedupe: don't ping /expose more than once per session/exp

/** Tiny event bus so pages can re-render after boot resolves. */
const listeners = new Set<() => void>();
export function onExperimentsLoaded(fn: () => void): () => void {
  if (primed) { fn(); return () => { /* noop */ }; }
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/**
 * Boot: hit /api/exp/active once, populate the cache. Safe to call
 * multiple times; second + third are no-ops while the first is in
 * flight (cached promise).
 */
let bootPromise: Promise<void> | null = null;
export function loadExperiments(): Promise<void> {
  if (bootPromise) return bootPromise;
  bootPromise = (async () => {
    try {
      const cookieId = getCookieId();
      const res = await fetch(`/api/exp/active?cookieId=${encodeURIComponent(cookieId)}`, {
        credentials: 'include',
      });
      if (!res.ok) return;
      const data = (await res.json()) as { assignments?: VariantPick[] };
      for (const a of data.assignments ?? []) {
        cache.set(a.experimentKey, a);
      }
    } catch {
      /* offline / backend down — pages stay on control */
    } finally {
      primed = true;
      for (const fn of listeners) {
        try { fn(); } catch { /* ignore one bad listener */ }
      }
      listeners.clear();
    }
  })();
  return bootPromise;
}

/**
 * Synchronous read. Returns the visitor's pick OR null if either:
 *   - boot hasn't completed
 *   - no experiment with that key is active
 *   - visitor is outside the traffic gate
 *
 * Renderers should check `getVariant` and fall back to control content
 * when null — never crash on absent assignment.
 */
export function getVariant(experimentKey: string): VariantPick | null {
  return cache.get(experimentKey) ?? null;
}

/**
 * `getPayload<T>(key, default)` — typed sugar around getVariant when the
 * payload shape is known to the caller.
 */
export function getPayload<T = Record<string, unknown>>(experimentKey: string, fallback: T): T {
  const v = cache.get(experimentKey);
  if (!v?.payload) return fallback;
  return v.payload as unknown as T;
}

/**
 * Mark the variant as exposed — i.e. the visitor actually saw the
 * variant-specific content. Idempotent across the session via local
 * dedupe + idempotent on the server.
 */
export function exposeVariant(experimentKey: string): void {
  const pick = cache.get(experimentKey);
  if (!pick) return;
  const dedupeKey = `${experimentKey}:${pick.variantKey}`;
  if (exposed.has(dedupeKey)) return;
  exposed.add(dedupeKey);
  try {
    fetch('/api/exp/expose', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        cookieId: getCookieId(),
        experimentKey,
        variantKey: pick.variantKey,
      }),
      keepalive: true,
    }).catch(() => { /* swallow */ });
  } catch { /* swallow */ }
}

/**
 * Client-side conversion event. For server-side events (signup, paid
 * IPN) the backend calls `trackExperimentEvent()` directly; this is
 * for events the server doesn't otherwise see (e.g. a modal open
 * doesn't touch auth, so we ping from here).
 */
export function trackEvent(metric: string, value: number = 1): void {
  try {
    fetch('/api/exp/event', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        cookieId: getCookieId(),
        metric,
        value,
      }),
      keepalive: true,
    }).catch(() => { /* swallow */ });
  } catch { /* swallow */ }
}
